import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import {
  Inject,
  Injectable,
  Optional,
  PLATFORM_ID,
  REQUEST,
  DOCUMENT
} from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { Request } from 'express';
import { Metadata } from '../interfaces/metadata';

@Injectable({
  providedIn: 'root',
})
export class MetaService {
  constructor(
    private meta: Meta,
    private title: Title,
    @Inject(PLATFORM_ID) private platformId: Object,
    @Inject(DOCUMENT) private document: Document,
    @Optional() @Inject(REQUEST) private request: Request, // Inject the request object
  ) {}

  setMetadata(metadata: Metadata) {
    // Set title
    this.setTitle(metadata.title || '');

    // Set description
    this.setDescription(metadata.description);

    // Set keywords
    this.setKeywords(metadata.keywords);

    // Set creator
    this.setMetaName('twitter:creator', 'imagebase.pro');

    // Set webpage url
    this.setWebpageUrl();

    // Set image
    const imageUrl =
      metadata.imageUrl || 'https://imagebase.pro/assets/og-ib.jpg';
    this.setImage(imageUrl);
  }

  private setTitle(title: string): void {
    this.title.setTitle(title);
    this.setMetaName('twitter:title', title);
    this.setMetaProperty('og:title', title);
  }

  private setDescription(description?: string): void {
    this.setMetaName('description', description);
    this.setMetaName('twitter:description', description);
    this.setMetaProperty('og:description', description);
  }

  private setKeywords(keywords?: string): void {
    this.setMetaName('keywords', keywords);
  }

  private setWebpageUrl(): void {
    let url: string = 'https://imagebase.pro/';
    if (isPlatformBrowser(this.platformId)) {
      url = window.location.href;
    } else if (isPlatformServer(this.platformId)) {
      url = this.request.url;
    }

    this.setMetaName('twitter:site', url);
    this.setMetaProperty('og:url', url);
    this.setCanonicalLink(url);
  }

  private setImage(imageUrl: string): void {
    this.setMetaName('twitter:image', imageUrl);
    this.setMetaProperty('og:image', imageUrl);
    this.setMetaProperty('og:image:secure_url', imageUrl);
    this.setMetaProperty(
      'og:image:type',
      this.getImageMimeTypeFromUrl(imageUrl),
    );
    this.setMetaProperty('og:image:width', '1200');
    this.setMetaProperty('og:image:height', '600');
  }

  private setMetaName(name: string, value?: string) {
    this.meta.removeTag(`name="${name}"`);
    if (value) {
      this.meta.addTag({ name, content: value });
    }
  }

  private setMetaProperty(property: string, value?: string) {
    this.meta.removeTag(`property="${property}"`);
    if (value) {
      this.meta.addTag({ property, content: value });
    }
  }

  private setCanonicalLink(url: string) {
    const link: HTMLLinkElement = this.document.createElement('link');
    link.rel = 'canonical';
    link.href = url;

    const existingLink = this.document.querySelector('link[rel="canonical"]');

    if (existingLink) {
      this.document.head.removeChild(existingLink);
    }

    this.document.head.appendChild(link);
  }

  private getImageMimeTypeFromUrl(url: string): string {
    // Define a map of file extensions to MIME types
    const mimeTypes: { [key: string]: string } = {
      jpg: 'image/jpeg',
      jpeg: 'image/jpeg',
      png: 'image/png',
      gif: 'image/gif',
      webp: 'image/webp',
      bmp: 'image/bmp',
      svg: 'image/svg+xml',
      tiff: 'image/tiff',
      ico: 'image/x-icon',
    };

    // Extract the file extension from the URL
    const extensionMatch = url.split('.').pop()?.toLowerCase();

    // Return the corresponding MIME type or null if not found
    return extensionMatch && mimeTypes[extensionMatch]
      ? mimeTypes[extensionMatch]
      : '';
  }
}
